import { ArrowLeft, Minus, Plus, Trash2 } from "lucide-react";

import { FormField } from "@/components/common/FormField";
import { NumericStepper } from "@/components/common/NumericStepper";
import { SelectField } from "@/components/common/SelectField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { accountLabel } from "@/features/accounts/accountLabel";
import type { Account } from "@/features/accounts/types";
import type { CartLine } from "@/features/vending/cart";
import { currencySymbol, formatMoney } from "@/lib/format";
import { cn } from "@/lib/utils";

const FIELD_CLASS =
  "h-11 w-full rounded-lg border border-input bg-card px-3 text-[15px] outline-none focus:ring-2 focus:ring-ring";

export function CartPanel({
  lines,
  total,
  currency,
  label,
  onLabelChange,
  accounts,
  accountId,
  onAccountChange,
  received,
  onReceivedChange,
  onChangeQuantity,
  onRemove,
  onClear,
  onCheckout,
  onContinue,
  onBackToSelection,
  step,
  isSubmitting,
}: {
  lines: CartLine[];
  total: number;
  currency: string;
  label: string;
  onLabelChange: (value: string) => void;
  accounts: Account[];
  accountId: string;
  onAccountChange: (value: string) => void;
  received: string;
  onReceivedChange: (value: string) => void;
  onChangeQuantity: (productId: string, delta: number) => void;
  onRemove: (productId: string) => void;
  onClear: () => void;
  onCheckout: () => void;
  onContinue: () => void;
  onBackToSelection: () => void;
  step: "selection" | "payment";
  isSubmitting: boolean;
}) {
  const receivedAmount = parseFloat(received) || 0;
  const changeDue = receivedAmount - total;
  const paymentSufficient = receivedAmount >= total;
  const paymentError = lines.length > 0 && received.trim() !== "" && !paymentSufficient;

  return (
    <section className="flex flex-col gap-4 rounded-xl border border-border bg-card p-4 lg:w-[380px] lg:shrink-0">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {step === "selection" ? "Étape 1 sur 2" : "Étape 2 sur 2"}
          </p>
          <h2 className="text-lg font-semibold">
            {step === "selection" ? "Sélection" : "Paiement"}
            {lines.length > 0 ? ` · ${lines.length} article${lines.length > 1 ? "s" : ""}` : ""}
          </h2>
        </div>
        {lines.length > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="text-sm font-medium text-destructive transition hover:opacity-80"
          >
            Vider
          </button>
        )}
      </div>

      {lines.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          Touchez un produit pour l’ajouter.
        </p>
      ) : (
        <ul className="flex flex-col">
          {lines.map((line) => (
            <li key={line.productId} className="flex items-center gap-2 border-b border-muted py-3 last:border-b-0">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{line.name}</p>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {formatMoney(line.unitPrice, currency)} l’unité
                </p>
              </div>

              <div className="flex items-center overflow-hidden rounded-lg border border-input">
                <button
                  type="button"
                  onClick={() => onChangeQuantity(line.productId, -1)}
                  className="grid h-10 w-9 place-items-center transition hover:bg-muted"
                  aria-label={`Retirer un ${line.name}`}
                >
                  <Minus size={16} />
                </button>
                <span className="w-9 text-center text-[15px] font-semibold tabular-nums">{line.quantity}</span>
                <button
                  type="button"
                  onClick={() => onChangeQuantity(line.productId, 1)}
                  disabled={line.quantity >= line.available}
                  className="grid h-10 w-9 place-items-center transition hover:bg-muted disabled:opacity-40"
                  aria-label={`Ajouter un ${line.name}`}
                >
                  <Plus size={16} />
                </button>
              </div>

              <span className="w-20 text-right text-[15px] font-semibold tabular-nums">
                {formatMoney(line.unitPrice * line.quantity, currency)}
              </span>

              <button
                type="button"
                onClick={() => onRemove(line.productId)}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-muted-foreground transition hover:bg-muted"
                aria-label={`Supprimer ${line.name}`}
              >
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="rounded-lg bg-muted/60 p-4">
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-medium">Total à payer</span>
          <span className="text-2xl font-semibold tabular-nums">{formatMoney(total, currency)}</span>
        </div>

        {step === "payment" && (
          <>
            <FormField
              label="Libellé de la vente"
              htmlFor="sale-label"
              hint="Repère la vente dans l’historique."
              hintId="sale-label-hint"
            >
              <Input
                id="sale-label"
                aria-describedby="sale-label-hint"
                value={label}
                onChange={(e) => onLabelChange(e.target.value)}
                placeholder="ex. Table 4 — service du soir"
              />
            </FormField>

            {accounts.length > 1 && (
              <div className="mt-4">
                <FormField label="Encaisser sur le compte" htmlFor="sale-account">
                  <SelectField
                    id="sale-account"
                    value={accountId}
                    onChange={onAccountChange}
                    options={accounts.map((account, index) => ({
                      value: account.id,
                      label: accountLabel(account, index),
                    }))}
                  />
                </FormField>
              </div>
            )}

            {/* Counting change out loud is the last step of every cash sale. */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <FormField
                label="Montant reçu"
                hint="Le montant doit couvrir le total."
                error={paymentError ? `Il manque ${formatMoney(Math.abs(changeDue), currency)}.` : undefined}
                htmlFor="received-amount"
                errorId="received-amount-error"
                hintId="received-amount-hint"
              >
                <NumericStepper
                  id="received-amount"
                  min={0}
                  step={100}
                  value={received}
                  onChange={(value) => onReceivedChange(String(value))}
                  placeholder="0"
                  suffix={currencySymbol(currency)}
                  aria-invalid={paymentError}
                  aria-describedby={paymentError ? "received-amount-error" : "received-amount-hint"}
                />
              </FormField>

              <div className="flex flex-col gap-2">
                <span className="text-[15px] font-medium">{paymentError ? "Montant manquant" : "À rendre"}</span>
                <div
                  className={cn(
                    "flex min-h-12 items-center rounded-lg border px-3 py-2 text-lg font-semibold tabular-nums [overflow-wrap:anywhere]",
                    paymentSufficient && receivedAmount > 0
                      ? "border-success/30 bg-success-soft text-success-foreground"
                      : paymentError
                        ? "border-destructive/40 bg-destructive-soft text-destructive"
                        : "border-input bg-card text-muted-foreground",
                  )}
                >
                  {paymentError
                    ? formatMoney(Math.abs(changeDue), currency)
                    : receivedAmount > 0
                      ? formatMoney(changeDue, currency)
                      : "—"}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {step === "selection" ? (
        <Button type="button" size="lg" className="w-full" disabled={lines.length === 0} onClick={onContinue}>
          Passer au paiement
        </Button>
      ) : (
        <div className="flex gap-3">
          <Button type="button" variant="outline" size="lg" onClick={onBackToSelection} disabled={isSubmitting}>
            <ArrowLeft />
            Retour
          </Button>
          <Button
            type="button"
            size="lg"
            className="flex-1"
            disabled={lines.length === 0 || isSubmitting || !paymentSufficient}
            onClick={onCheckout}
          >
            Encaisser {total > 0 ? formatMoney(total, currency) : ""}
          </Button>
        </div>
      )}
    </section>
  );
}
